import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface SkillsMasteredReResponse {
  skills_mastered: {
    average_by_level: number[]
    lesson_ranges: string[]
    weakest_lesson: Nullable<number>
  }
}

export type SkillsMasteredReData = SkillsMasteredReResponse['skills_mastered']

export async function skillsMasteredRe(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<SkillsMasteredReData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('skills_mastered', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SkillsMasteredReResponse>(url, query)
  if (!data) return null

  const { skills_mastered } = data
  if (skills_mastered.average_by_level.every((average) => average === 0)) return null

  return skills_mastered
}

export interface SkillsMasteredMsResponse {
  skills_mastered: {
    data: { total: number; average: number }
    fractions: { total: number; average: number }
    geometry: { total: number; average: number }
    measurement: { total: number; average: number }
    number: { total: number; average: number }
    operations: { total: number; average: number }
  }
}

export type SkillsMasteredMsData = SkillsMasteredMsResponse['skills_mastered']

export async function skillsMasteredMs(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<SkillsMasteredMsData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('skills_mastered', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SkillsMasteredMsResponse>(url, query)
  if (!data) return null

  const { skills_mastered } = data
  const emptyData = skills_mastered.data.total === 0 && skills_mastered.data.average === 0
  const emptyFractions = skills_mastered.fractions.total === 0 && skills_mastered.fractions.average === 0
  const emptyGeometry = skills_mastered.geometry.total === 0 && skills_mastered.geometry.average === 0
  const emptyMeasurement = skills_mastered.measurement.total === 0 && skills_mastered.measurement.average === 0
  const emptyNumber = skills_mastered.number.total === 0 && skills_mastered.number.average === 0
  const emptyOperations = skills_mastered.operations.total === 0 && skills_mastered.operations.average === 0
  if (emptyData && emptyFractions && emptyGeometry && emptyMeasurement && emptyNumber && emptyNumber && emptyOperations)
    return null

  return skills_mastered
}
